import { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Flex, Heading, Button } from '@radix-ui/themes';

import { UpgradeBanner } from '../../../../shared/UpgradeBanner/index.jsx';
import { ToggleGroup } from '../../../../shared/ToggleGroup/ToggleGroup';

import { useFetchBroadcastMessages } from '../../hooks/useFetchBroadcastMessages';
import { useSavePaymentInfo } from '../../hooks/useSavePaymentInfo';
import { PAID } from '../../utils/constants';

import { Table } from './Table.jsx';
import BroadcastCalendarView from './CalendarView/BroadcastCalendarView.jsx';

const VIEW_TYPES = {
  TABLE: 'table',
  CALENDAR: 'calendar',
};

const STORAGE_KEY = 'broadcastsActiveView';

const BroadcastMessagesTable = () => {
  const [viewType, setViewType] = useState(
    () => window.localStorage.getItem(STORAGE_KEY) || VIEW_TYPES.TABLE,
  );
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation('broadcastMessages');

  // Minimal hook call to get tier info for UpgradeBanner
  const {
    tier,
    canSendBroadcast,
    nextFreeBroadcastDate,
  } = useFetchBroadcastMessages({
    page: 1,
    itemsPerPage: 1,
    showAll: false,
  });

  const onClick = () => {
    queryClient.removeQueries({ queryKey: ['broadcastMessage'] });
    history.push('/edit/audience?from=broadcasts');
  };

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, viewType);
  }, [viewType]);

  useEffect(() => {
    ReactTooltip.rebuild();
  });

  useSavePaymentInfo();

  return (
    <>
      {tier && tier !== PAID && (
        <Flex justify="center" mt="25px" p="0 24px">
          <UpgradeBanner
            icon="fa-regular fa-paper-plane"
            titleKey="broadcast.title"
            countValue={canSendBroadcast ? 1 : 0}
            countLimit={1}
            textContent={(t, Trans) => (
              <>
                {canSendBroadcast ? (
                  <>
                    {t('broadcast.text1')}
                    <br />
                    <Trans
                      i18nKey="broadcast.text2"
                      components={{ strong: <strong /> }}
                      ns="upgradeBanners"
                    />
                  </>
                ) : (
                  <Trans
                    i18nKey="broadcast.text3"
                    components={{ strong: <strong /> }}
                    ns="upgradeBanners"
                    values={{
                      date: nextFreeBroadcastDate,
                    }}
                  />
                )}
              </>
            )}
          />
        </Flex>
      )}
      <Container>
        <TableSection>
          <Flex justify="between" align="center" mb="24px">
            <Heading size="6" m="0">
              {t('indexHeader')}
            </Heading>
            <Flex gap="3" align="center">
              <ToggleGroup
                items={[
                  {
                    value: VIEW_TYPES.TABLE,
                    label: t('viewToggle.table'),
                    icon: 'fa-regular fa-list',
                  },
                  {
                    value: VIEW_TYPES.CALENDAR,
                    label: t('viewToggle.calendar'),
                    icon: 'fa-regular fa-calendar',
                  },
                ]}
                value={viewType}
                onValueChange={setViewType}
              />
            </Flex>
            <Button size="3" variant="solid" onClick={onClick}>
              <i className="fa-solid fa-plus" />
              {t('indexButton')}
            </Button>
          </Flex>

          {viewType === VIEW_TYPES.TABLE ? (
            <Table />
          ) : (
            <BroadcastCalendarView />
          )}
        </TableSection>
      </Container>
    </>
  );
};

export default BroadcastMessagesTable;

const Container = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 24px;
`;

const TableSection = styled.div`
  width: ${props => (props.$hasBanner ? '65%' : '100%')};
  padding: ${props => (props.$hasBanner ? '0 0 24px 0' : '0 24px 24px 24px')};
`;
